// Scorecard band display. Colours come from the band tokens (tokens.css), never inline values,
// and a band is never shown by colour alone: every badge carries an icon and a label.
import { CircleCheckIcon, CircleDashedIcon, CircleMinusIcon, CircleXIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "../cn";

/** Mirrors `Band` in @companyos/domain-kpi (kept structural so the UI stays domain-free). */
export type Band = "great" | "regular" | "average" | "low" | "insufficient_data";

export const BANDS: readonly Band[] = ["great", "regular", "average", "low", "insufficient_data"];

export const BAND_LABELS: Record<Band, string> = {
  great: "Great",
  regular: "On track",
  average: "Average",
  low: "Low",
  insufficient_data: "Awaiting data",
};

/** CSS colour for SVG fills and strokes (Recharts props), resolved from the band tokens. */
export function bandColor(band: Band): string {
  return `var(--band-${band.replace("_", "-")})`;
}

/** Static class names so Tailwind sees each one. */
const BAND_SWATCH: Record<Band, string> = {
  great: "bg-band-great",
  regular: "bg-band-regular",
  average: "bg-band-average",
  low: "bg-band-low",
  insufficient_data: "bg-band-insufficient-data",
};

const BAND_ICON: Record<
  Band,
  React.ComponentType<{ className?: string; "aria-hidden"?: boolean }>
> = {
  great: CircleCheckIcon,
  regular: CircleCheckIcon,
  average: CircleMinusIcon,
  low: CircleXIcon,
  insufficient_data: CircleDashedIcon,
};

/**
 * The band as a bare dot (design spec §8.2 `BandDot`), for dense rows such as the Scorecard
 * sidebar's DE list. The dot is labelled (`role="img"`), so the band is never colour alone.
 */
export function BandDot({
  band,
  size = 8,
  className,
  ...props
}: { band: Band; size?: 6 | 8 | 10 } & Omit<React.ComponentProps<"span">, "children">) {
  return (
    <span
      data-slot="band-dot"
      data-band={band}
      role="img"
      aria-label={BAND_LABELS[band]}
      title={BAND_LABELS[band]}
      className={cn(
        "inline-block shrink-0 rounded-full",
        size === 6 ? "size-1.5" : size === 10 ? "size-2.5" : "size-2",
        BAND_SWATCH[band],
        className,
      )}
      {...props}
    />
  );
}

export function BandBadge({
  band,
  className,
  ...props
}: { band: Band } & Omit<React.ComponentProps<"span">, "children">) {
  const Icon = BAND_ICON[band];
  return (
    <span
      data-slot="band-badge"
      data-band={band}
      className={cn(
        "inline-flex h-6 shrink-0 items-center gap-1.5 rounded-sm border border-border bg-card px-2 text-xs font-ui whitespace-nowrap text-secondary",
        className,
      )}
      {...props}
    >
      <span className={cn("size-2 shrink-0 rounded-full", BAND_SWATCH[band])} aria-hidden="true" />
      <Icon className="size-3.5 text-muted-foreground" aria-hidden={true} />
      {BAND_LABELS[band]}
    </span>
  );
}
